import React, { useState } from 'react';
import { Box, Typography, Avatar, IconButton, Menu, MenuItem, Chip, Tooltip } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router-dom';
import ActivityReactions from '../common/ActivityReactions';
import { addPostReaction, removePostReaction, deletePost } from '../../services/api';
import PostComposer from './PostComposer';
import SubscriberBadge from '../common/SubscriberBadge';
import { useUser } from '../../contexts/UserContext';
import { isPro } from '../../utils/subscription';

export interface PostData {
  id: number;
  user_id: number;
  username: string;
  display_name: string;
  avatar: string | null;
  game: {
    id: number;
    name: string;
    cover_image: string | null;
  } | null;
  content: string;
  reaction_counts: { [key: number]: number } | null;
  reply_count: number;
  visibility: number;
  created_at: string;
  updated_at: string;
  user_reaction: number | null;
  is_edited?: boolean;
  edit_expires_at?: string | null;
}

interface PostCardProps {
  post: PostData;
  currentUserId?: number | null;
  isMyPage?: boolean;
  isProfile?: boolean;
  onDelete?: (postId: number) => void;
  onReactionUpdate?: (postId: number, newReactions: { [key: number]: number }, newUserReaction: number | null) => void;
  onEdit?: (postId: number) => void;
}

const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const months = Math.floor(days / 30);

  if (minutes < 1) return 'たった今';
  if (minutes < 60) return `${minutes}分前`;
  if (hours < 24) return `${hours}時間前`;
  if (days < 30) return `${days}日前`;
  if (months < 12) return `${months}ヶ月前`;
  return `${Math.floor(days / 365)}年前`;
};

const getCoverUrl = (coverId: string | null) => {
  if (!coverId) return '/image/no_image.png';
  if (coverId.startsWith('pulse_')) {
    return `https://plsdb.com/cover/${coverId.replace('pulse_', '')}.jpg`;
  }
  return `https://images.igdb.com/igdb/image/upload/t_cover_big/${coverId}.jpg`;
};

const PostCard: React.FC<PostCardProps> = ({ post, currentUserId, isMyPage, isProfile, onDelete, onReactionUpdate, onEdit }) => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const isOwner = isMyPage ?? (currentUserId === post.user_id);
  const coverUrl = isProfile && post.game?.cover_image ? getCoverUrl(post.game.cover_image) : null;

  // 編集メニューの状態判定
  // - hasPaidPlan: PRO以上か
  // - withinWindow: 編集期限内か (edit_expires_at が未来)
  // - canEdit: 実際に編集できるか
  const hasPaidPlan = isPro(user?.subscription_plan ?? 0);
  const withinWindow = !!post.edit_expires_at && new Date(post.edit_expires_at).getTime() > Date.now();
  const canEdit = isOwner && hasPaidPlan && withinWindow;

  const handleDelete = async () => {
    setMenuAnchor(null);
    try {
      await deletePost(post.id);
      onDelete?.(post.id);
    } catch (error) {
      console.error('ポストの削除に失敗しました:', error);
    }
  };

  const handleEditClick = () => {
    setMenuAnchor(null);
    setIsEditing(true);
  };

  const handleEditDone = () => {
    setIsEditing(false);
    onEdit?.(post.id);
  };

  return (
    <Box
      sx={{
        px: { xs: 1, sm: 2 },
        pt: { xs: 1, sm: 2 },
        pb: { xs: 0.5, sm: 1 },
        borderBottom: '1px solid',
        borderColor: 'divider',
        // 非公開（自分のポスト）は左に縦ボーダー＋薄い背景で「自分にしか見えない」を視覚化
        ...(post.visibility === 2 && isOwner && {
          backgroundColor: 'rgba(255, 152, 0, 0.04)',
        }),
        '&:hover': {
          backgroundColor: post.visibility === 2 && isOwner
            ? 'rgba(255, 152, 0, 0.07)'
            : 'rgba(255, 255, 255, 0.02)',
        },
        transition: 'background-color 0.15s',
      }}
    >
      {/* ヘッダー: アバター + ユーザー情報 + 時間 + メニュー */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1, sm: 1.5 } }}>
        <Avatar
          src={coverUrl || post.avatar || undefined}
          variant={coverUrl ? 'rounded' : 'circular'}
          sx={{
            width: { xs: 36, md: 44 }, height: { xs: 36, md: 44 },
            cursor: 'pointer',
            bgcolor: 'grey.800', fontSize: '0.9rem',
            ...(coverUrl && { borderRadius: 1 }),
          }}
          onClick={() => coverUrl ? navigate(`/game/${post.game!.id}`) : navigate(`/profile/${post.username}`)}
        >
          {(post.display_name || post.username)?.[0]?.toUpperCase()}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* ユーザー名 + 時間 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, minWidth: 0 }}>
            <Typography
              variant="body2"
              noWrap
              sx={{
                fontWeight: 600, cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
                fontSize: '0.75rem',
                lineHeight: 1.2,
                flexShrink: 1,
                minWidth: 0,
              }}
              onClick={() => navigate(`/profile/${post.username}`)}
            >
              {post.display_name || post.username}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.75rem', lineHeight: 1, flexShrink: 0, display: { xs: 'none', sm: 'block' } }}>
              @{post.username}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.6rem', lineHeight: 1, flexShrink: 0 }}>
              · {formatTimeAgo(post.created_at)}
            </Typography>
            {post.is_edited && (
              <Typography variant="caption" color="pulse.info" noWrap sx={{ fontSize: '0.6rem', lineHeight: 1, flexShrink: 0, fontStyle: 'italic' }}>
                · 編集済み
              </Typography>
            )}
            {post.visibility === 1 && (
              isOwner ? (
                <Tooltip title="サイレント：フォロワーのTLには流れません（プロフィール等では公開）" arrow>
                  <Chip
                    icon={<VolumeOffIcon sx={{ fontSize: '0.7rem !important' }} />}
                    label="サイレント"
                    size="small"
                    variant="outlined"
                    sx={{
                      height: 18,
                      fontSize: '0.6rem',
                      borderRadius: '4px',
                      color: 'text.secondary',
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                      '& .MuiChip-icon': { ml: 0.5, mr: -0.25, color: 'text.secondary' },
                      '& .MuiChip-label': { px: 0.75 },
                      flexShrink: 0,
                    }}
                  />
                </Tooltip>
              ) : (
                <Tooltip title="サイレント：フォロワーのTLに流れない投稿" arrow>
                  <VolumeOffIcon sx={{ fontSize: '0.85rem', color: 'text.secondary', flexShrink: 0 }} />
                </Tooltip>
              )
            )}
            {post.visibility === 2 && (
              <Tooltip title="非公開：自分だけが閲覧できます" arrow>
                <Chip
                  icon={<VisibilityOffIcon sx={{ fontSize: '0.7rem !important' }} />}
                  label="非公開"
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.6rem',
                    borderRadius: '4px',
                    '& .MuiChip-icon': { ml: 0.5, mr: -0.25,  },
                    '& .MuiChip-label': { px: 0.75 },
                    flexShrink: 0,
                  }}
                />
              </Tooltip>
            )}

            {/* メニュー（自分のポストのみ） */}
            {isOwner && (
              <IconButton
                size="small"
                sx={{ ml: 'auto', p: 0.5, flexShrink: 0 }}
                onClick={(e) => setMenuAnchor(e.currentTarget)}
              >
                <MoreHorizIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
            )}
          </Box>

          {isEditing ? (
            <Box sx={{ mt: 0.5, mb: 0.5 }}>
              <PostComposer
                mode="edit"
                postId={post.id}
                embedded
                initialContent={post.content}
                initialGame={post.game ? { game_id: post.game.id, name: post.game.name, cover: post.game.cover_image } : null}
                initialVisibility={post.visibility}
                onPostUpdated={handleEditDone}
                onCancel={() => setIsEditing(false)}
              />
            </Box>
          ) : (
            <>
              {/* コンテンツ */}
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  lineHeight: 1.35,
                  mb: post.game ? 1 : 0,
                }}
              >
                {post.content}
              </Typography>

              {/* ゲームタグ */}
              {post.game && (
                <Chip
                  icon={<SportsEsportsIcon sx={{ fontSize: '0.85rem !important' }} />}
                  label={post.game.name}
                  size="small"
                  onClick={() => navigate(`/game/${post.game!.id}`)}
                  sx={{
                    height: { xs: 20, sm: 24 },
                    color: 'text.secondary',
                    fontSize: { xs: '0.6rem', sm: '0.65rem' },
                    backgroundColor: 'rgba(255, 255, 255, 0.06)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                    cursor: 'pointer',
                    mb: 0.5,
                  }}
                />
              )}

              {/* リアクション */}
              <ActivityReactions
                targetId={post.id}
                reactions={post.reaction_counts || {}}
                userReaction={post.user_reaction}
                isPublic={!isOwner}
                addReaction={addPostReaction}
                removeReaction={removePostReaction}
                onReactionUpdate={onReactionUpdate}
              />
            </>
          )}
        </Box>
      </Box>

      {/* 編集/削除メニュー */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem
          onClick={handleEditClick}
          disabled={!canEdit}
          sx={{ fontSize: '0.85rem' }}
        >
          編集
          <SubscriberBadge variant="inline" sx={{ ml: 1 }} />
          {hasPaidPlan && !withinWindow && (
            <Typography variant="caption" sx={{ ml: 0.5, color: 'text.disabled', fontSize: '0.7rem' }}>
              (期限切れ)
            </Typography>
          )}
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main', fontSize: '0.85rem' }}>
          削除
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default PostCard;
